import * as React from 'react';
import { FC } from 'react';

import { useLoyaltyOffersUiCooldownContentQuery } from 'generated/sanity-graphql';
import { useNavigation } from 'hooks/navigation/use-navigation';
import { useRoute } from 'hooks/navigation/use-route';
import { useIsMobileBreakpoint } from 'hooks/use-media-query';
import { selectors, useAppSelector } from 'state/global-state';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { DIFFERENCE_TIME_UNITS, getDifferenceToNow } from 'utils/dateTime';
import { maybeLocale, maybeLocaleRaw } from 'utils/graphql';
import { routes } from 'utils/routing';

import { interpolateSanityKey } from '../utils';

import {
  CTAButton,
  CooldownDrawerContainer,
  Description,
  Image,
  Overlay,
  Terms,
  Title,
} from './loyalty-offers-cooldown-drawer.styled';

const LoyaltyOffersCooldownDrawer: FC<React.PropsWithChildren<unknown>> = () => {
  const { linkTo } = useNavigation();
  const { pathname } = useRoute();
  const { loyaltyUser } = useLoyaltyUser();
  const isMobile = useIsMobileBreakpoint();
  const loyaltyOffersInCooldown = useAppSelector(selectors.loyalty.selectLoyaltyOffersInCooldown);
  const { data: cooldownContentData } = useLoyaltyOffersUiCooldownContentQuery({
    variables: {
      id: 'feature-loyalty-offers-ui-singleton',
    },
    skip: !loyaltyOffersInCooldown,
  });

  const offerCooldownContent = cooldownContentData?.LoyaltyOffersUI?.globalOfferCooldownContent;

  const cooldownContent = {
    image: maybeLocale(offerCooldownContent?.image),
    title: maybeLocale(offerCooldownContent?.title) || '',
    description: maybeLocaleRaw(offerCooldownContent?.description) || '',
    buttonText: maybeLocale(offerCooldownContent?.actionButton?.buttonText) || '',
    buttonUrl: maybeLocale(offerCooldownContent?.actionButton?.buttonPath) || '',
    termsText: maybeLocaleRaw(offerCooldownContent?.terms),
  };

  const handlePress = () => {
    linkTo(cooldownContent.buttonUrl.trim());
  };

  const isOffersPage = pathname.includes(routes.offers);
  const showCooldown = loyaltyOffersInCooldown && isOffersPage;
  const diffInMin = getDifferenceToNow(
    DIFFERENCE_TIME_UNITS.MINUTES,
    loyaltyUser?.offerRedemptionAvailability?.availableAfter || ''
  );

  return showCooldown ? (
    <Overlay testID="offers-cooldown-drawer-overlay">
      <CooldownDrawerContainer testID="offers-cooldown-drawer-container">
        <Image image={cooldownContent.image} alt="" />
        <Title>{cooldownContent.title}</Title>
        <Description testID="offers-cooldown-drawer-description">
          {interpolateSanityKey(cooldownContent.description, 'REMAINING_MINUTES', diffInMin)}
        </Description>
        {isMobile && <CTAButton onPress={handlePress}>{cooldownContent.buttonText}</CTAButton>}
        <Terms>{cooldownContent.termsText}</Terms>
      </CooldownDrawerContainer>
    </Overlay>
  ) : null;
};

export default LoyaltyOffersCooldownDrawer;
